import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Search, Trash2, Pencil, X, Send, Eye, EyeOff, GraduationCap, Mail, Phone, MapPin, Upload, ClipboardPaste,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status meta: colour + label ordered by workflow
const STATUS_META = {
  not_contacted: { label: 'Not contacted', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  emailed:       { label: 'Emailed',       color: 'bg-blue-100 text-blue-800 border-blue-200' },
  replied:       { label: 'Replied',       color: 'bg-amber-100 text-amber-800 border-amber-200' },
  booked:        { label: 'Booked',        color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  declined:      { label: 'Declined',      color: 'bg-red-100 text-red-800 border-red-200' },
  completed:     { label: 'Completed',     color: 'bg-primary/10 text-primary border-primary/20' },
};
const STATUS_ORDER = Object.keys(STATUS_META);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  return Number.isFinite(d) ? d : null;
};

const EMPTY_FORM = { clinic_name: '', contact_name: '', email: '', phone: '', address: '', notes: '', training_status: 'not_contacted', booking_date: '', completed_date: '' };

export const VetClinics = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };

  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vet-clinics`, { headers: auth });
      setClinics(data.clinics || []);
    } catch (e) {
      logger.error('vet clinics load', e);
      if (e.response?.status === 401) navigate('/admin/login');
      else toast.error('Failed to load clinics');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clinics.filter((c) => {
      if (statusFilter !== 'all' && (c.training_status || 'not_contacted') !== statusFilter) return false;
      if (!q) return true;
      return (
        (c.clinic_name || '').toLowerCase().includes(q) ||
        (c.contact_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      );
    });
  }, [clinics, search, statusFilter]);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      clinic_name: c.clinic_name || '',
      contact_name: c.contact_name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
      notes: c.notes || '',
      training_status: c.training_status || 'not_contacted',
      booking_date: c.booking_date || '',
      completed_date: c.completed_date || '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.clinic_name.trim() || !form.email.trim()) {
      toast.error('Clinic name and email are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty-string date fields — Pydantic prefers null over ""
      ['booking_date', 'completed_date'].forEach((k) => { if (!payload[k]) payload[k] = null; });
      if (editingId) {
        await axios.put(`${API}/admin/vet-clinics/${editingId}`, payload, { headers: auth });
        toast.success('Clinic updated');
      } else {
        await axios.post(`${API}/admin/vet-clinics`, payload, { headers: auth });
        toast.success('Clinic added');
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.clinic_name} from the address book? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/vet-clinics/${c.id}`, { headers: auth });
      toast.success('Clinic removed');
      await load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const changeStatus = async (c, newStatus) => {
    try {
      await axios.put(`${API}/admin/vet-clinics/${c.id}`, { training_status: newStatus }, { headers: auth });
      await load();
    } catch (e) { toast.error('Status update failed'); }
  };

  const composeTo = (c) => {
    navigate(`/admin/email-composer?template=training&to=${encodeURIComponent(c.email)}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold text-primary">Vet Clinics</h1>
                  <p className="text-xs sm:text-sm text-foreground/60">
                    Address book + training outreach log
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn">
                <Upload className="w-4 h-4 mr-1.5" /> Bulk import
              </Button>
              <Button onClick={openNew} data-testid="add-clinic-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add clinic
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="bg-white border border-border rounded-xl p-4 mb-5 flex items-center gap-3 flex-wrap" data-testid="vet-clinic-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by clinic, vet, or email"
              className="pl-9"
              data-testid="clinic-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="clinic-status-filter"
            className="px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All statuses</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-foreground/60">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-12 text-center">
            <GraduationCap className="w-14 h-14 text-foreground/30 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">No clinics yet</h2>
            <p className="text-sm text-foreground/60 mb-5">
              Add clinics you'd like to run training with — you'll then be able to email them and track outreach in one place.
            </p>
            <Button onClick={openNew}><Plus className="w-4 h-4 mr-1.5" /> Add your first clinic</Button>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="vet-clinic-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Clinic</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Last outreach</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((c) => {
                    const status = c.training_status || 'not_contacted';
                    const meta = STATUS_META[status];
                    const emailedDays = daysAgo(c.last_emailed_at);
                    return (
                      <tr key={c.id} data-testid={`clinic-row-${c.id}`} className="hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-foreground">{c.clinic_name}</div>
                          {c.address && (
                            <div className="text-xs text-foreground/50 flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3" /> {c.address}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-foreground">{c.contact_name || '—'}</div>
                          <div className="text-xs text-foreground/60 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" /> {c.email}
                          </div>
                          {c.phone && (
                            <div className="text-xs text-foreground/50 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {c.phone}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={status}
                            onChange={(e) => changeStatus(c, e.target.value)}
                            data-testid={`clinic-status-${c.id}`}
                            className={`px-2 py-1 text-xs font-semibold rounded-full border ${meta.color} focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer`}
                          >
                            {STATUS_ORDER.map((s) => (
                              <option key={s} value={s}>{STATUS_META[s].label}</option>
                            ))}
                          </select>
                          {status === 'booked' && c.booking_date && (
                            <div className="text-[11px] text-emerald-700 mt-1">on {fmtDate(c.booking_date)}</div>
                          )}
                          {status === 'completed' && c.completed_date && (
                            <div className="text-[11px] text-primary mt-1">on {fmtDate(c.completed_date)}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {c.last_emailed_at ? (
                            <div>
                              <div className="text-foreground/80 text-xs">
                                {fmtDate(c.last_emailed_at)}
                                {emailedDays != null && emailedDays > 0 && (
                                  <span className="text-foreground/50 ml-1">· {emailedDays}d ago</span>
                                )}
                              </div>
                              <div className="text-[11px] text-foreground/50 truncate max-w-[240px]" title={c.last_email_subject}>
                                {c.last_email_subject || ''}
                              </div>
                              {c.last_email_open_count > 0 ? (
                                <div className="inline-flex items-center gap-1 text-[11px] text-emerald-700 mt-0.5">
                                  <Eye className="w-3 h-3" /> Opened {c.last_email_open_count}×
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1 text-[11px] text-foreground/40 mt-0.5">
                                  <EyeOff className="w-3 h-3" /> Unopened
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-foreground/40">Never emailed</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => composeTo(c)}
                              data-testid={`clinic-compose-${c.id}`}
                              title="Compose training email"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-primary/40 text-primary rounded hover:bg-primary/5"
                            >
                              <Send className="w-3 h-3" /> Email
                            </button>
                            <button
                              onClick={() => openEdit(c)}
                              data-testid={`clinic-edit-${c.id}`}
                              title="Edit"
                              className="p-1.5 rounded hover:bg-gray-100 text-foreground/60"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => remove(c)}
                              data-testid={`clinic-delete-${c.id}`}
                              title="Remove"
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="clinic-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-base font-semibold">{editingId ? 'Edit clinic' : 'Add clinic'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Clinic name *</Label>
                <Input
                  value={form.clinic_name}
                  onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
                  placeholder="Island Vet Clinic"
                  data-testid="form-clinic-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vet / contact</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Dr. Emma Rose"
                    data-testid="form-contact-name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="clinic@example.com"
                    data-testid="form-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 345 555 0101"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <select
                    value={form.training_status}
                    onChange={(e) => setForm({ ...form, training_status: e.target.value })}
                    data-testid="form-status"
                    className="w-full h-10 px-3 border border-border rounded-md text-sm bg-white"
                  >
                    {STATUS_ORDER.map((s) => (
                      <option key={s} value={s}>{STATUS_META[s].label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="West Bay Road, Grand Cayman"
                  data-testid="form-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Booking date</Label>
                  <Input
                    type="date"
                    value={form.booking_date || ''}
                    onChange={(e) => setForm({ ...form, booking_date: e.target.value })}
                    data-testid="form-booking-date"
                  />
                </div>
                <div>
                  <Label>Completed date</Label>
                  <Input
                    type="date"
                    value={form.completed_date || ''}
                    onChange={(e) => setForm({ ...form, completed_date: e.target.value })}
                    data-testid="form-completed-date"
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  data-testid="form-notes"
                  className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Preferred time slots, key contacts, anything worth remembering…"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="form-save">
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add clinic'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk import modal */}
      {bulkOpen && (
        <BulkImportModal
          onClose={() => setBulkOpen(false)}
          onImported={async () => { setBulkOpen(false); await load(); }}
          auth={auth}
        />
      )}
    </div>
  );
};

export default VetClinics;

// ---------------------------------------------------------------------------
// Bulk Import modal — paste CSV or upload a .csv file. Preview parsed rows
// before hitting the backend. Header row is expected (order flexible).
// ---------------------------------------------------------------------------
const REQUIRED_HEADERS = ['clinic_name', 'email'];
const OPTIONAL_HEADERS = ['contact_name', 'phone', 'address', 'notes'];
const CSV_TEMPLATE = 'clinic_name,contact_name,email,phone,address,notes\nIsland Vet Clinic,Dr. Emma Rose,clinic@example.com,+1 345 555 0101,West Bay Road,Prefers mornings\n';

// Small CSV parser — handles quoted fields with embedded commas/newlines.
const parseCSV = (text) => {
  const rows = [];
  let cur = [];
  let field = '';
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuote) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuote = false; }
      } else { field += c; }
      continue;
    }
    if (c === '"') { inQuote = true; continue; }
    if (c === ',') { cur.push(field); field = ''; continue; }
    if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      cur.push(field);
      if (cur.length > 1 || cur[0] !== '') rows.push(cur);
      cur = []; field = '';
      continue;
    }
    field += c;
  }
  if (field !== '' || cur.length) { cur.push(field); rows.push(cur); }
  return rows;
};

const BulkImportModal = ({ onClose, onImported, auth }) => {
  const [mode, setMode] = useState('paste');
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState(null); // {rows: [...], errors: [...]}
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => { setCsvText(e.target.result); parse(e.target.result); };
    reader.readAsText(file);
  };

  const parse = (text) => {
    const rows = parseCSV(text || csvText);
    if (rows.length < 2) { setParsed({ rows: [], errors: ['Not enough rows — need a header + at least 1 data row'] }); return; }
    const headers = rows[0].map((h) => h.trim().toLowerCase());
    const missing = REQUIRED_HEADERS.filter((r) => !headers.includes(r));
    if (missing.length) { setParsed({ rows: [], errors: [`Missing required column(s): ${missing.join(', ')}`] }); return; }
    const idx = (name) => headers.indexOf(name);
    const parsedRows = [];
    const errors = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      if (r.every((c) => !c || !c.trim())) continue;
      const record = {};
      [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].forEach((h) => {
        const p = idx(h);
        if (p >= 0) record[h] = (r[p] || '').trim() || undefined;
      });
      if (!record.clinic_name) { errors.push(`Row ${i + 1}: missing clinic_name`); continue; }
      if (!record.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(record.email)) { errors.push(`Row ${i + 1}: invalid email "${record.email || ''}"`); continue; }
      parsedRows.push(record);
    }
    setParsed({ rows: parsedRows, errors });
  };

  const submit = async () => {
    if (!parsed?.rows?.length) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/vet-clinics/bulk-import`, { rows: parsed.rows }, { headers: auth });
      const saved = data.saved?.length || 0;
      const skipped = data.skipped?.length || 0;
      if (saved > 0) toast.success(`Imported ${saved} clinic${saved === 1 ? '' : 's'}`);
      if (skipped > 0) toast.error(`Skipped ${skipped}: ${data.skipped.slice(0, 3).map((s) => `${s.email} (${s.reason})`).join(', ')}${skipped > 3 ? '…' : ''}`);
      await onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setSubmitting(false); }
  };

  const copyTemplate = () => { setCsvText(CSV_TEMPLATE); parse(CSV_TEMPLATE); };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Bulk import clinics</h2>
            <p className="text-xs text-foreground/60">Paste a CSV with header row, or upload a .csv file</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-4">
          <div className="inline-flex items-center rounded-lg border border-border overflow-hidden text-sm mb-4">
            <button
              onClick={() => setMode('paste')}
              data-testid="bulk-mode-paste"
              className={`px-3 py-1.5 flex items-center gap-1 ${mode === 'paste' ? 'bg-primary text-white' : 'bg-white text-foreground/70'}`}
            >
              <ClipboardPaste className="w-4 h-4" /> Paste
            </button>
            <button
              onClick={() => setMode('upload')}
              data-testid="bulk-mode-upload"
              className={`px-3 py-1.5 flex items-center gap-1 ${mode === 'upload' ? 'bg-primary text-white' : 'bg-white text-foreground/70'}`}
            >
              <Upload className="w-4 h-4" /> Upload .csv
            </button>
          </div>
        </div>
        <div className="px-5 pb-2">
          {mode === 'paste' ? (
            <textarea
              value={csvText}
              onChange={(e) => { setCsvText(e.target.value); }}
              onBlur={() => parse(csvText)}
              rows={7}
              placeholder={CSV_TEMPLATE}
              data-testid="bulk-csv-textarea"
              className="w-full text-xs font-mono px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          ) : (
            <label className="w-full flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg py-8 cursor-pointer hover:border-primary hover:bg-primary/5">
              <Upload className="w-8 h-8 text-foreground/40 mb-2" />
              <span className="text-sm font-medium">Click to choose a .csv file</span>
              <span className="text-xs text-foreground/50 mt-1">Header row required</span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="bulk-csv-upload" />
            </label>
          )}
          <div className="flex items-center justify-between mt-2">
            <button onClick={copyTemplate} data-testid="bulk-insert-example" className="text-xs text-primary hover:underline">Insert example CSV</button>
            <Button variant="outline" size="sm" onClick={() => parse(csvText)} data-testid="bulk-parse-btn">Preview rows</Button>
          </div>
        </div>
        {parsed && (
          <div className="px-5 py-3 border-t border-border max-h-64 overflow-y-auto" data-testid="bulk-preview">
            {parsed.errors.length > 0 && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 mb-2">
                <div className="font-semibold mb-1">Issues in your CSV:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {parsed.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            {parsed.rows.length > 0 ? (
              <table className="w-full text-xs" data-testid="bulk-preview-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-2 py-1">Clinic</th>
                    <th className="text-left px-2 py-1">Contact</th>
                    <th className="text-left px-2 py-1">Email</th>
                    <th className="text-left px-2 py-1">Phone</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {parsed.rows.map((r, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1 font-medium">{r.clinic_name}</td>
                      <td className="px-2 py-1 text-foreground/70">{r.contact_name || '—'}</td>
                      <td className="px-2 py-1 text-foreground/70">{r.email}</td>
                      <td className="px-2 py-1 text-foreground/50">{r.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : parsed.errors.length === 0 && (
              <div className="text-xs text-foreground/50 text-center py-3">No valid rows yet — paste or upload a CSV.</div>
            )}
          </div>
        )}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <div className="text-xs text-foreground/60">
            {parsed?.rows?.length ? `${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'} ready` : 'Preview to see rows'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!parsed?.rows?.length || submitting}
              data-testid="bulk-import-submit"
            >
              {submitting ? 'Importing…' : `Import ${parsed?.rows?.length || 0}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
